// Utilities
import {computed, MaybeRef, onMounted, toValue} from "vue";
import {defineStore} from 'pinia'
import {useFirestore} from "vuefire";
import {where} from 'firebase/firestore'

import {useStorage} from "@vueuse/core";
import {Product} from "@/lib/keys";
import {loadBucketCollectionItemBy, loadBucketCollectionToRef, loadCollectionToRef, loadDocumentsBy} from "@/lib/utils";
import {Features, LIMITATIONS} from "@/keys";


export const useProductStore = defineStore('product-store', () => {
  const collectionName = 'products';
  const firestore = useFirestore();
  const products = useStorage<Product[]>('p', []);

  const filterByBrandSlug = (brandSlug: MaybeRef) => computed(() => products.value.filter(p => p.brand.slug === toValue(brandSlug)))
  const filterByMerchantSlug = (merchantSlug: MaybeRef) => computed(() => {
    return products.value.filter(p => {
      const ids = p.prices
        .filter(c => c && !isNaN(c.price) && c.price < LIMITATIONS.MAX_PRICE)
        .map(c => c.merchant_id);

      const slugs = p.merchants
        .filter(m => ids.includes(m.id))
        .map(m => m.slug);

      return slugs.includes(toValue(merchantSlug));
    })
  });

  const getBySlug = async (productSlug: string): Promise<Product[]> => {
    if (products.value && products.value.length >= 0) {
      const internalMatch = products.value.find(
        p => p.slug === productSlug,
      );

      if (internalMatch) {
        return Promise.resolve([internalMatch]);
      }

    }

    if (Features.isLocal) {
      console.log('Using local Firestore adapter for collection.')
      return await loadDocumentsBy<Product>(firestore, collectionName, [
        where('slug', '==', productSlug),
      ]);
    }

    return [await loadBucketCollectionItemBy<Product>(collectionName, productSlug)]
  };

  onMounted(
    Features.isLocal
      ? loadCollectionToRef(firestore, products, collectionName)
      : loadBucketCollectionToRef(products, collectionName),
  );

  return {
    products,
    getBySlug,
    filterByBrandSlug,
    filterByMerchantSlug,
  }
});
